import { Link, useLocation, type LinkProps } from 'react-router-dom';

/**
 * A link that opens its destination in the side drawer.
 *
 * ## What it actually does
 *
 * Pushes the same URL a plain `<Link>` would, and carries the page you are on
 * in `location.state.background`. `AppRoutes` renders the background as the
 * page and the URL as a drawer over it. Nothing about the address changes, so
 * copying it, sharing it or reloading it all behave the way they did before -
 * a cold load has no background and gets the full page.
 *
 * ## Opening a second one keeps the first background
 *
 * `background ?? location` rather than always `location`. Clicking a track
 * inside an album drawer would otherwise record the album's URL as the page
 * behind, and closing would land on a page nobody had been looking at.
 *
 * ## Deliberately not automatic
 *
 * A `<Link to="/tracks/1">` still navigates. Whether something belongs in a
 * drawer is a judgement about that link - the dock's title bar opens one, an
 * "open full page" link must not - and a router-level rule that guessed would
 * be wrong somewhere unnoticeable.
 */
export function DrawerLink({ to, children, ...rest }: LinkProps) {
  return (
    <Link to={to} state={useDrawerState()} {...rest}>
      {children}
    </Link>
  );
}

/**
 * The state a link needs to open in the drawer, for callers that cannot be a
 * `DrawerLink`.
 *
 * `LinkButton` is the one that matters: "Edit profile" is a button, and
 * wrapping it in another anchor to get this behaviour would nest two links.
 * Exported rather than copied so there is still exactly one definition of what
 * "open in a drawer" means.
 */
export function useDrawerState(): { background: unknown } {
  const location = useLocation();
  const state = location.state as { background?: unknown } | null;

  // `background ?? location`, so opening a second drawer keeps the page the
  // first one was over rather than recording the first drawer's URL.
  return { background: state?.background ?? location };
}
